import { useStartLogin, useStopLogin } from "@/api/mutations/logins";
import type { LoginItemType } from "@/api/queries/logins/types";
import { queryKeys } from "@/api/queryKeys";
import { Switch } from "@/components/ui/switch";
import { globalInterval } from "@/lib/date";
import { useQueryClient } from "@tanstack/react-query";
import { formatDuration, intervalToDuration } from "date-fns";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

interface LoginRunningStateProps {
  loginItem: LoginItemType;
}

export const LoginRunningState = ({ loginItem }: LoginRunningStateProps) => {
  const queryClient = useQueryClient();
  const { mutateAsync: startLogin, isPending: isStarting } = useStartLogin(loginItem.id);
  const { mutateAsync: stopLogin, isPending: isStopping } = useStopLogin(loginItem.id);

  const statusRef = useRef<HTMLSpanElement | null>(null);

  const statusText = () => {
    if (loginItem.running) {
      return "Running";
    } else if (loginItem.stopAt) {
      const stopAt = new Date(loginItem.stopAt);
      const endAt = new Date(stopAt);
      endAt.setMinutes(endAt.getMinutes() + 15);
      const now = new Date();

      if (now.getTime() >= endAt.getTime()) {
        return "Pending...";
      }

      return `Starts in ${formatDuration(
        intervalToDuration({
          start: now,
          end: endAt,
        }),
      )}`;
    }
  };

  useEffect(() => {
    const statusEl = statusRef.current;

    if (!statusEl) {
      return;
    }

    if (loginItem.running) {
      statusEl.innerText = "Running";
    } else if (loginItem.stopAt) {
      const stopAt = new Date(loginItem.stopAt);
      const endAt = new Date(stopAt);
      endAt.setMinutes(endAt.getMinutes() + 15);

      const callback = async () => {
        const now = new Date();
        if (now.getTime() >= endAt.getTime()) {
          await queryClient.invalidateQueries({ queryKey: [queryKeys.getLogins] });

          statusEl.innerText = "Pending...";
          return;
        }

        statusEl.innerText = `Starts in ${formatDuration(
          intervalToDuration({
            start: now,
            end: endAt,
          }),
        )}`;
      };
      void callback();
      return globalInterval.listen(callback);
    }
  }, [loginItem.running, loginItem.stopAt]);

  const isPending = isStarting || isStopping;
  const handleToggle = async (login: LoginItemType) => {
    try {
      if (login.running) {
        await stopLogin();
      } else {
        await startLogin();
      }

      toast.success(`${!login.running ? "Account Started" : "Account stopped for 15 minutes"}`);
    } catch {
      toast.error(`Failed to ${!login.running ? "start" : "stop"} login.`);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Switch
        highlighted
        checked={!!loginItem.running}
        onCheckedChange={() => handleToggle(loginItem)}
        disabled={isPending}
      />
      <span className="text-sm" ref={statusRef}>
        {statusText()}
      </span>
    </div>
  );
};
